import React from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import MCPBridge from './mcp/MCPBridge';
import AppShellLegacy from './components/AppShell';
import AppShellNew from './components/AppShellNew';
import Dashboard from './components/Dashboard';
import ApplicantsList from './components/ApplicantsList';
import ApplicantDetail from './components/ApplicantDetail';
import AgentWorkspace from './components/AgentWorkspace';
import AdminPanel from './components/AdminPanel';
import ScreeningChecks from './components/ScreeningChecks';
import CaseManagement from './components/CaseManagement';
import { CaseDetailPage } from './components/cases';
import CaseOperations from './pages/CaseOperations';
import LoginPage from './components/LoginPage';
import LoadingScreen from './components/LoadingScreen';
import { ErrorBoundary } from './components/ErrorBoundary';
import { NotFoundPage } from './components/shared/NotFound';
import { useGlobalRealtimeUpdates } from './hooks/useRealtimeUpdates';
import { CompanyDetail } from './components/companies';

// Page components
import {
  CompaniesPage,
  IntegrationsPage,
  DeviceIntelligencePage,
  ReusableKYCPage,
  AnalyticsPage,
  SettingsPage,
  BillingPage,
  AuditLogPage,
  QuestionnairesPage,
  BiometricsPage,
  WorkflowBuilderPage,
  AlertsPage,
} from './components/pages';
import DevWorkbenchPage from './pages/DevWorkbenchPage';

// KYC Share components
import { ReusableKYCPage as ApplicantKYCSharePage } from './components/kyc-share';

// SDK components (public, no auth required)
import SDKVerifyPage from './components/sdk/SDKVerifyPage';
import SDKEmbedPage from './components/sdk/SDKEmbedPage';
import SDKDemoPage from './components/sdk/SDKDemoPage';
import SessionVerifyPage from './components/sdk/SessionVerifyPage';
import LivenessHandoffPage from './components/sdk/LivenessHandoffPage';
import MediaPipeTestPage from './components/sdk/MediaPipeTestPage';
import PassiveLivenessTestPage from './components/sdk/PassiveLivenessTestPage';

// Public pages (no auth required)
import DocumentUploadPage from './components/DocumentUploadPage';
import AcceptInvitePage from './components/AcceptInvitePage';

// Developer Portal - Landing page (authenticated routes redirect to main dashboard)
import PortalLanding from './portal/pages/PortalLanding';
import './portal/portal.css';

// Feature flag: Set to true to use new Hyperkit-styled AppShell
const USE_NEW_APPSHELL = true;
const AppShell = USE_NEW_APPSHELL ? AppShellNew : AppShellLegacy;

// Redirect component for deprecated portal routes
function PortalRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWithRedirect } = useAuth0();

  React.useEffect(() => {
    const path = location.pathname;

    // Map old portal routes to new unified Dev Workbench
    const redirects = {
      '/portal/login': () => loginWithRedirect(),
      '/portal/signup': () => loginWithRedirect({ authorizationParams: { screen_hint: 'signup' } }),
      '/portal/dashboard': '/dev-workbench',
      '/portal/explorer': '/dev-workbench?tab=explorer',
      '/portal/keys': '/dev-workbench?tab=keys',
      '/portal/webhooks': '/dev-workbench?tab=webhooks',
      '/portal/logs': '/dev-workbench?tab=logs',
      '/portal/quickstart': '/dev-workbench?tab=quickstart',
      '/portal/usage': '/billing',
      '/portal/billing': '/billing',
    };

    const redirect = redirects[path];
    if (typeof redirect === 'function') {
      redirect();
    } else if (redirect) {
      navigate(redirect, { replace: true });
    } else {
      // Unknown portal route, go to Dev Workbench
      navigate('/dev-workbench', { replace: true });
    }
  }, [location.pathname, navigate, loginWithRedirect]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0a0a0f', color: '#8b919e' }}>
      Redirecting...
    </div>
  );
}

export default function App() {
  const location = useLocation();

  // Portal landing page (public marketing page - keep as-is)
  if (location.pathname === '/portal') {
    return <PortalLanding />;
  }

  // Other portal routes redirect to unified dashboard
  if (location.pathname.startsWith('/portal/')) {
    return <PortalRedirect />;
  }

  // SDK routes are public - render them without auth check
  if (location.pathname.startsWith('/sdk/') || location.pathname.startsWith('/v/')) {
    return (
      <Routes>
        <Route path="/sdk/verify" element={<SDKVerifyPage />} />
        <Route path="/sdk/embed" element={<SDKEmbedPage />} />
        <Route path="/sdk/demo" element={<SDKDemoPage />} />
        <Route path="/sdk/liveness/:token" element={<LivenessHandoffPage />} />
        <Route path="/sdk/test-liveness" element={<MediaPipeTestPage />} />
        <Route path="/sdk/test-passive-liveness" element={<PassiveLivenessTestPage />} />
        <Route path="/v/:token" element={<SessionVerifyPage />} />
      </Routes>
    );
  }

  // Public document upload page (no auth required)
  if (location.pathname.startsWith('/upload/')) {
    return (
      <Routes>
        <Route path="/upload/:token" element={<DocumentUploadPage />} />
      </Routes>
    );
  }

  // Accept invite page (public, but may trigger Auth0 login)
  if (location.pathname === '/accept-invite') {
    return (
      <Routes>
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
      </Routes>
    );
  }

  // All other routes require authentication
  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  const { isAuthenticated, isLoading, user, logout } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();

  // Enable real-time WebSocket updates when authenticated
  useGlobalRealtimeUpdates();

  // Show skeleton loading screen while checking auth state
  if (isLoading) {
    return <LoadingScreen message="Checking authentication..." variant="skeleton" />;
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Determine current page from URL for sidebar highlighting
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path.startsWith('/admin')) return 'admin';
    if (path.startsWith('/agent')) return 'agent';
    if (path.startsWith('/applicants')) return 'applicants';
    if (path.startsWith('/screening')) return 'screening';
    if (path.startsWith('/alerts')) return 'alerts';
    if (path.startsWith('/case-operations')) return 'case-operations';
    if (path.startsWith('/cases')) return 'cases';
    if (path.startsWith('/companies')) return 'companies';
    if (path.startsWith('/integrations')) return 'integrations';
    if (path.startsWith('/dev-workbench')) return 'dev-workbench';
    if (path.startsWith('/device-intel')) return 'device-intel';
    if (path.startsWith('/reusable-kyc')) return 'reusable-kyc';
    if (path.startsWith('/analytics')) return 'analytics';
    if (path.startsWith('/settings')) return 'settings';
    if (path.startsWith('/billing')) return 'billing';
    if (path.startsWith('/audit-log')) return 'audit-log';
    if (path.startsWith('/questionnaires')) return 'questionnaires';
    if (path.startsWith('/biometrics')) return 'biometrics';
    if (path.startsWith('/workflow-builder')) return 'workflow-builder';
    return 'dashboard';
  };

  const handleNavigate = (page) => {
    const routes = {
      dashboard: '/',
      agent: '/agent',
      admin: '/admin',
      applicants: '/applicants',
      companies: '/companies',
      screening: '/screening',
      alerts: '/alerts',
      cases: '/cases',
      'case-operations': '/case-operations',
      integrations: '/integrations',
      'dev-workbench': '/dev-workbench',
      'device-intel': '/device-intel',
      'reusable-kyc': '/reusable-kyc',
      analytics: '/analytics',
      settings: '/settings',
      billing: '/billing',
      'audit-log': '/audit-log',
      questionnaires: '/questionnaires',
      biometrics: '/biometrics',
      'workflow-builder': '/workflow-builder',
    };
    navigate(routes[page] || '/');
  };

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  return (
    <AppShell
      currentPage={getCurrentPage()}
      onNavigate={handleNavigate}
      user={user}
      onLogout={handleLogout}
    >
      <MCPBridge />
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/agent" element={<AgentWorkspace />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/applicants" element={<ApplicantsList />} />
          <Route path="/applicants/:id" element={<ApplicantDetail />} />
          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/companies/:id" element={<CompanyDetail />} />
          <Route path="/screening" element={<ScreeningChecks />} />
          <Route path="/alerts" element={<AlertsPage />} />
          <Route path="/cases" element={<CaseManagement />} />
          <Route path="/cases/:id" element={<CaseDetailPage />} />
          <Route path="/case-operations" element={<CaseOperations />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/dev-workbench" element={<DevWorkbenchPage />} />
          <Route path="/device-intel" element={<DeviceIntelligencePage />} />
          <Route path="/reusable-kyc" element={<ReusableKYCPage />} />
          <Route path="/applicants/:applicantId/share" element={<ApplicantKYCSharePage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/audit-log" element={<AuditLogPage />} />
          <Route path="/questionnaires" element={<QuestionnairesPage />} />
          <Route path="/biometrics" element={<BiometricsPage />} />
          <Route path="/workflow-builder" element={<WorkflowBuilderPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ErrorBoundary>
    </AppShell>
  );
}
